export const ENDPOINTS = {
    auth: {
        signIn: '/users/login/' as const,
        refresh: '/token/refresh/' as const,
        signUp: '/users/sign-up/' as const,
        confirmEmail: '/users/verify-email/' as const,
        initiatePasswordRecovery: '/users/reset-password-request/' as const,
        resetPassword: '/users/reset-password/' as const,
        checkResetPasswordParams: '/users/reset-password-check/' as const,
        google: '/gauth/' as const,
    } as const,
    tools: {
        lotSizeCalculator: {
            calculate: '/tools/lot-size-calculator/calculate/' as const,
        },
    } as const,
    serviceStatus: {
        get: '/service-status/' as const,
    } as const,
    ai: {
        query: '/ai/query/' as const,
        conversations: '/ai/conversations/' as const,
        conversationDetail: (id: string) => `/ai/conversations/${id}/` as const,
        messages: (conversationId: string) =>
            `/ai/conversations/${conversationId}/messages/` as const,
        messageFeedback: (messageId: string) =>
            `/ai/messages/${messageId}/feedback/` as const,
        credits: '/ai/credits/' as const,
        creditPacks: '/ai/credits/packs/' as const,
        creditsPurchase: '/ai/credits/purchase/' as const,
        shareConversation: (id: string) =>
            `/ai/conversations/${id}/share/` as const,
    } as const,
    viewer: {
        me: '/users/me/' as const,
        updateAvatar: (id: number) => `/users/${id}/` as const,
        updateProfileData: (id: number) => `/users/${id}/` as const,
        changePassword: (id: number) =>
            `/users/${id}/change-password/` as const,
        deleteUserAccount: '/users/delete' as const,
    } as const,
    affiliate: {
        referralMe: '/referral/me/' as const,
        referralEnroll: '/referral/me/enroll/' as const,
        referralCommissions: '/referral/me/commissions/' as const,
        referralReferrals: '/referral/me/referrals/' as const,
        referralPayoutProfile: '/referral/me/payout-profile/' as const,
        referralPayoutRequest: '/referral/me/payout-request/' as const,
        referralAnalytics: '/referral/me/analytics/' as const,
        referralCustomPlan: '/referral/me/custom-plan-request/' as const,
        referralDetail: (referralId: number | string) =>
            `/referral/me/referrals/${referralId}/` as const,
    } as const,
    tradingView: {
        getAllSymbols: '/tickers/',
        getHistory: '/iqfeed_data/',
        getRealtimeData: '/iqfeed_data/real_time/',
        getAllChartLayouts: '/chart_layouts/' as const,
        getChartLayoutContent: (id: number) => `/chart_layouts/${id}/` as const,
        saveChartLayout: '/chart_layouts/' as const,
        updateChartLayoutContent: (id: number) =>
            `/chart_layouts/${id}/` as const,
        removeChartLayout: (id: number) => `/chart_layouts/${id}/` as const,
        getAllStudyTemplates: '/chart_templates/' as const,
        getStudyTemplate: (name: string) => `/chart_templates/${name}` as const,
        saveStudyTemplate: '/chart_templates/' as const,
        removeStudyTemplate: (name: string) =>
            `/chart_templates/${name}` as const,
    } as const,
    watchlist: {
        getAllTickers: '/watchlist_tickers/' as const,
        getFavouriteTickers: '/watchlist/' as const,
        addFavouriteTicker: '/watchlist/' as const,
        removeFavouriteTicker: (id: number) => `/watchlist/${id}/` as const,
        reorderFavouriteTickers: '/watchlist_bulk/' as const,
        removeFavouriteTickers: '/watchlist_bulk/' as const,
    } as const,
    patternWatchlist: {
        getAll: '/pattern_watchlist_table/' as const,
        create: '/pattern_watchlist_table/' as const,
        update: (id: number) => `/pattern_watchlist_table/${id}/` as const,
        delete: (id: number) => `/pattern_watchlist_table/${id}/` as const,
    } as const,
    chatbot: {
        sendMessage: '/chatbot/' as const,
    } as const,
    subscription: {
        getInvoices: '/stripe/invoices/' as const,
        getCustomerPortal: '/stripe/customer-portal/' as const,
        migrationStatus: '/subscription/migration-status/' as const,
        migrate: '/subscription/migrate/' as const,
        accountLimits: '/subscription/account-limits/' as const,
        selectAccount: '/subscription/select-account/' as const,
    } as const,
    tradingPlatformConnection: {
        getMT5Accounts: '/trading_journal/mt5-accounts/' as const,
        attachMT5Account: '/trading_journal/mt5-accounts/attach/' as const,
        unattachAccount: (accountId: number) =>
            `/trading_journal/${accountId}/delete/` as const,
        getCTraderAccounts: '/trading_journal/ctrader-accounts/' as const,
        attachCTraderAccount:
            '/trading_journal/ctrader-accounts/attach/' as const,
        unattachCTraderAccount: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/delete/` as const,
        getMT4Accounts: '/trading_journal/mt4-accounts/' as const,
        attachMT4Account: '/trading_journal/mt4-accounts/attach/' as const,
        unattachMT4Account: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/delete/` as const,
    } as const,
    tradingPlatform: {
        // Account info
        getAccountInfo: (accountId: number) =>
            `/trading_journal/${accountId}/info` as const,
        getCTraderAccountInfo: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/info` as const,
        getMT4AccountInfo: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/info` as const,
        // Stats
        getAccountStats: (accountId: number) =>
            `/trading_journal/${accountId}/stats/` as const,
        getCTraderAccountStats: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/stats/` as const,
        getMT4AccountStats: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/stats/` as const,
        // Calendar
        getAccountCalendar: (accountId: number) =>
            `/trading_journal/${accountId}/calendar/` as const,
        getCTraderAccountCalendar: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/calendar/` as const,
        getMT4AccountCalendar: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/calendar/` as const,
        // Note
        getAccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/${accountId}/note/` as const,
        getCTraderAccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/note/` as const,
        getMT4AccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/note/` as const,
        // Save note
        saveAccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/${accountId}/note/` as const,
        saveCTraderAccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/note/` as const,
        saveMT4AccountCalendarNoteByDate: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/note/` as const,
        // Save currency
        saveAccountCurrency: (accountId: number) =>
            `/trading_journal/${accountId}/currency/` as const,
        saveCTraderAccountCurrency: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/currency/` as const,
        saveMT4AccountCurrency: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/currency/` as const,
        // Balance history - All providers
        getBalanceHistory: (accountId: number) =>
            `/trading_journal/${accountId}/balance_history/` as const,
        getCTraderBalanceHistory: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/balance_history/` as const,
        getMT4BalanceHistory: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/balance_history/` as const,
        // Live trades - All providers
        getLiveTrades: (accountId: number) =>
            `/trading_journal/${accountId}/live_trades/` as const,
        getCTraderLiveTrades: (accountId: number) =>
            `/trading_journal/ctrader/${accountId}/live_trades/` as const,
        getMT4LiveTrades: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/live_trades/` as const,
        // Upload MT4 statement
        uploadMT4Statement: (accountId: number) =>
            `/trading_journal/mt4/${accountId}/upload_statement/` as const,
    } as const,
    tableColumns: {
        getTableColumns: (tableId: string) =>
            `/user_preferences/?table_name=${tableId}` as const,
        updateTableColumns: (tableId: string) =>
            `/user_preferences/?table_name=${tableId}` as const,
    } as const,
    pattern: {
        getPatterns: '/pattern_watchlist',
        deletePattern: (id: number) => `/pattern_watchlist/${id}/`,
        updatePattern: (id: number) => `/pattern_watchlist/${id}/`,
        getByPatternWatchlistId: (id: number) =>
            `/pattern_watchlist?pattern_watchlist_table=${id}` as const,
        toggleFlag: (id: number) => `/pattern_watchlist/${id}/` as const,
        searchPatterns: '/search_patterns' as const,
        savePattern: '/pattern_watchlist/' as const,
        getDistinctFilterValues: '/get_distinct_filter_values/' as const,
    } as const,
    candles: {
        getMasterSubPatternCandles: '/master_subpattern_candles/' as const,
    },
    patternLive: {
        getLivePatterns: '/get-live-pattern/' as const,
        getLiveDistinctFilterValues: '/get-redis-pattern-structure/' as const,
        getLivePatternStats: '/get-live-pattern-stats/' as const,
    },
    newsCalendar: {
        getNews: '/news-calendar/' as const,
        getNewsById: (id: number) => `/news-calendar/${id}/` as const,
        getRelatedNews: (id: number) =>
            `/news-calendar/${id}/related_news/` as const,
        getAnalysis: (id: number) => `/news-calendar/${id}/analysis/` as const,
    },
    financialNews: {
        getAll: '/financial-news/' as const,
        getById: (id: number) => `/financial-news/${id}/` as const,
    },
    notification: {
        getAll: '/notification/' as const,
        markAsRead: (id: number) => `/notification/${id}/read/` as const,
        markAllAsRead: '/notification/read-all/' as const,
        markAsDelivered: (id: number) =>
            `/notification/${id}/delivered/` as const,
    },
    session: {
        list: '/sessions/' as const,
        revoke: '/sessions/revoke/' as const,
        revokeAll: '/sessions/revoke-all/' as const,
    },
} as const;

export const CMS_ENDPOINTS = {
    getQuestionMark: () => `/question-marks` as const,
    getLegend: () => `/legends` as const,
    getBrokerRequest: () => `/globals/broker-request` as const,
    getDisclamers: () => `/globals/disclamers` as const,
    postForm: () => `/form-submissions` as const,
    getConnectionTutorial: () => `/connection-tutorials` as const,
    getColumnName: () => `/column-names` as const,
    getNews: () => `/news` as const,
    getOnboardingSteps: () => `/onboarding-steps` as const,
    getPlatformFeatures: () => `/platform-features` as const,
    getFooter: () => `/globals/footer` as const,
    getReportBug: () => `/globals/report-bug` as const,
    getReviewForm: () => `/globals/review-form` as const,
};
